import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import LockIcon from '@mui/icons-material/Lock';
import {
    Alert,
    Box,
    Button,
    Menu,
    MenuItem,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import {
    POLYGON_RECTANGLE,
    POLYGON_MAP,
    POLYGON_OBSTACLE,
} from 'common/components/segment/constants';
import {
    checkCallback,
    checkCallbackError,
    editionModeUseEffect,
    getDictionaryById,
    selectedSegmentIdUseEffect,
} from 'common/components/segment/helpers';
import { useBreakpoint } from 'common/hooks';

import { DEFAULT_MAP_PREVIEW_ZOOM } from '../../../common/constants';
import * as actions from '../actions';
import {
    buildShapes,
    copyPasteEventsUseEffect,
    drawingModeUseEffect,
    editSegmentDataUseEffect,
    mapValueUseEffect,
} from '../helpers';
import * as selectors from '../selectors';

import StyledMap from './StyledMap';
import {
    createTerraDrawInstance,
    TerraDrawController,
} from './TerraDraw/terraDrawManager';

const MapWrapper = ({
    checkSegment,
    commercialOfferId,
    createSegment,
    currentProjectLocation,
    drawingType,
    editSegmentData,
    handleOnClickModule,
    handleOnCopy,
    handleOnPaste,
    isDisabledEdition,
    isDisablingModules,
    isDrawingMode,
    isEditionMode,
    isEnabledSimulation,
    myForwardedRef,
    onOpenDrawer,
    prepareEditSegment,
    resetForm,
    segments,
    selectSegmentById,
    selectedSegmentId,
    setIsDisablingModules,
    setSelectedItems,
    sunPosition,
    updateAzimuth,
    updateFallOrientation,
    viewLayersStatus,
}) => {
    const { t } = useTranslation();

    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();
    const map = useMap();

    const center = {
        lat: Number(get(currentProjectLocation, 'latitude', 20.9664281)),
        lng: Number(get(currentProjectLocation, 'longitude', -89.6239336)),
    };
    const refBox = useRef(null);
    const [tempSegments, setTempSegments] = useState({});
    const [contextMenu, setContextMenu] = useState(null);
    const [menuOptions, setMenuOptions] = useState([]);
    const breakpoint = useBreakpoint();
    const [localController, setLocalController] = useState(null);

    const isDay = !isEnabledSimulation || sunPosition.elevation > 0;

    const handleClose = () => setContextMenu(null);

    const handleContextMenu = ({ azimuth, event, metadata, segmentId }) => {
        const { clientX, clientY } = event;
        event.preventDefault();
        let options = [
            {
                label: t('Set azimuth to this edge ({{azimuth}}°)', {
                    azimuth,
                }),
                onClick: () =>
                    updateAzimuth({
                        azimuth,
                        callback: handleClose,
                        commercialOfferId,
                        google,
                        map,
                        segmentId,
                    }),
            },
        ];

        const { drawing_type } = metadata;

        if (drawing_type === POLYGON_RECTANGLE) {
            options = [
                {
                    label: t(
                        'Set fall orientation to this edge ({{azimuth}}°)',
                        {
                            azimuth,
                        },
                    ),
                    onClick: () =>
                        updateFallOrientation({
                            azimuth,
                            callback: handleClose,
                            commercialOfferId,
                            google,
                            map,
                            segmentId,
                        }),
                },
            ];
        }

        setMenuOptions(options);
        setContextMenu(
            contextMenu === null
                ? { mouseX: clientX + 2, mouseY: clientY - 6 }
                : null,
        );
    };

    const handleCheckSegment = ({ evt, polygon, segmentId } = {}) => {
        checkSegment({
            callback: (id, modules) =>
                checkCallback(id, modules, myForwardedRef),
            callbackError: (id, values) =>
                checkCallbackError(id, values, myForwardedRef),
            commercialOfferId,
            evt,
            google,
            map,
            polygon,
            segmentId,
        });
    };

    useImperativeHandle(myForwardedRef, () => ({
        deleteShape: (id) => {
            if (tempSegments[id]) tempSegments[id]?.setMap(null);
            const newSegments = Object.keys(tempSegments)
                .filter((key) => key !== id)
                .reduce(
                    (acc, key) => ({ ...acc, [key]: tempSegments[key] }),
                    {},
                );
            setTempSegments(newSegments);
        },
        getZoomMap: () => map && map.getZoom(),
        resetSegmentPosition: (id, values) =>
            tempSegments[id]?.resetPosition(values, true),
        setModulesToSegment: (id, solarModules) =>
            tempSegments[id]?.setSolarModules(solarModules),
        self: () => map,
    }));

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        if (!map) return;
        const tdInstance = createTerraDrawInstance({ google, map });
        setLocalController(new TerraDrawController(tdInstance));
    }, [map]);

    useEffect(
        copyPasteEventsUseEffect({
            commercialOfferId,
            handleOnCopy,
            handleOnPaste,
            map,
            refBox,
        }),
        [refBox],
    );

    useEffect(
        mapValueUseEffect({
            currentProjectLocation,
            google,
            mapValue: map,
        }),
        [currentProjectLocation, map, google],
    );

    useEffect(() => {
        for (const [, segment] of Object.entries(tempSegments)) {
            segment.setSunPosition(sunPosition);
        }
    }, [sunPosition]);

    useEffect(() => {
        if (!segments || !map || !google) return;
        segments.forEach((segment) => {
            if (!tempSegments[segment.id]) return;

            if (
                segment.type === POLYGON_OBSTACLE &&
                tempSegments[segment.id].setHeight
            ) {
                tempSegments[segment.id].setHeight(
                    segment.height,
                    segment?.zIndex || 0,
                );
            }

            if (segment.type === POLYGON_MAP) {
                const isLocked = segment.locked ?? false;
                tempSegments[segment.id]?.setOptions({
                    draggable: !isLocked,
                    editable: !isLocked,
                });
            }
        });

        buildShapes({
            checkSegment: handleCheckSegment,
            google,
            handleOnClickModule,
            isDisabledEdition,
            mapRef: myForwardedRef,
            onContextMenu: handleContextMenu,
            prepareEditSegment: (value) => prepareEditSegment(value, google),
            segments,
            selectSegmentById,
            setSelectedItems,
            setTempSegments,
            sunPosition,
            tempSegments,
        });
    }, [segments, map, tempSegments]);

    useEffect(
        drawingModeUseEffect({
            breakpoint,
            createSegment: (polygon) => {
                createSegment({
                    commercialOfferId,
                    google,
                    mapRef: myForwardedRef,
                    polygon,
                });
            },
            drawingType,
            google,
            isDrawingMode,
            map: map,
            onOpenDrawer,
            localController,
        }),
        [isDrawingMode, drawingType, map, localController],
    );

    useEffect(
        selectedSegmentIdUseEffect({
            segmentsDictionary: getDictionaryById(segments),
            selectedSegmentId,
            tempSegments,
        }),
        [selectedSegmentId],
    );

    useEffect(
        editSegmentDataUseEffect({
            editSegmentData,
            tempSegments,
        }),
        [editSegmentData],
    );

    useEffect(
        editionModeUseEffect({
            breakpoint,
            isDisabledEdition,
            isDisablingModules,
            isEditionMode,
            onOpenDrawer,
            selectedSegmentId,
            tempSegments,
            viewLayersStatus,
        }),
        [
            isDisabledEdition,
            isDisablingModules,
            isEditionMode,
            selectedSegmentId,
            tempSegments,
            viewLayersStatus,
        ],
    );

    useEffect(() => {
        if (!google || !map) return;
        map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
    }, [google, map]);

    const onContextMenu = (e) => e.preventDefault();

    const onDblclick = () => resetForm();

    const menuOpen = Boolean(contextMenu);

    const anchorPosition = menuOpen
        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
        : null;

    const sx = {
        opacity: isDay ? 1 : 0.5,
        transition: 'opacity 0.2s linear',
    };

    return (
        <>
            <StyledMap>
                {isDisabledEdition && (
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            zIndex: 1000,
                        }}
                    >
                        <Alert icon={<LockIcon />} severity="warning">
                            {t('Edition not available')}
                        </Alert>
                    </Box>
                )}

                {isDisablingModules && (
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            zIndex: 1000,
                        }}
                    >
                        <Alert
                            action={
                                <Button
                                    onClick={() => setIsDisablingModules(false)}
                                    size="small"
                                >
                                    {t('Exit')}
                                </Button>
                            }
                            sx={{
                                background: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? '#1B1C20'
                                        : 'rgb(218, 221, 234)',
                                '& .MuiAlert-icon': {
                                    color: '#737fb9',
                                },
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? '#fff'
                                        : 'inherit',
                                mb: 0,
                            }}
                            icon={false}
                            severity="info"
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="body2" fontWeight="bold">
                                    {t('Disable modules mode activated')}
                                </Typography>
                            </Box>
                        </Alert>
                    </Box>
                )}

                <Box
                    onContextMenu={onContextMenu}
                    ref={refBox}
                    sx={{ ...sx, height: '100%', width: '100%' }}
                >
                    <Map
                        defaultCenter={center}
                        defaultZoom={DEFAULT_MAP_PREVIEW_ZOOM}
                        disableDefaultUI={isEditionMode || isDisabledEdition}
                        disableDoubleClickZoom={true}
                        fullscreenControl={false}
                        mapId={import.meta.env.VITE_GOOGLE_MAPS_DEFAULT_ID}
                        mapTypeControl={false}
                        onDblclick={onDblclick}
                        renderingType="VECTOR"
                        rotateControl={false}
                        streetViewControl={false}
                    />
                </Box>
            </StyledMap>

            <Menu
                anchorPosition={anchorPosition}
                anchorReference="anchorPosition"
                onClose={handleClose}
                open={menuOpen}
            >
                {menuOptions.map((option, index) => (
                    <MenuItem
                        key={`context-option-${index}`}
                        onClick={option.onClick}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    currentProjectLocation: selectors.getCurrentProjectLocationData,
    editSegmentData: selectors.getEditSegmentData,
    selectedSegmentId: selectors.getSelectedSegmentId,
    viewLayersStatus: selectors.getViewLayersStatus,
});

const mapDispatchToProps = (dispatch) => ({
    checkSegment: (config) => dispatch(actions.checkSegment(config)),
    createSegment: (config) => dispatch(actions.createSegment(config)),
    handleOnClickModule: (config) =>
        dispatch(actions.handleOnClickModule(config)),
    handleOnCopy: () => dispatch(actions.handleOnCopy()),
    handleOnPaste: (config) => dispatch(actions.handleOnPaste(config)),
    prepareEditSegment: (segmentId, google) =>
        dispatch(actions.prepareEditSegment(segmentId, google)),
    resetForm: () => dispatch(actions.resetForm()),
    selectSegmentById: (id) => dispatch(actions.selectSegmentById(id)),
    setSelectedItems: (config) => dispatch(actions.setSelectedItems(config)),
    updateAzimuth: (values) => dispatch(actions.updateAzimuth(values)),
    updateFallOrientation: (values) =>
        dispatch(actions.updateFallOrientation(values)),
    setIsDisablingModules: (isDisabling) =>
        dispatch(actions.setIsDisablingModules(isDisabling)),
});

MapWrapper.propTypes = {
    checkSegment: PropTypes.func,
    commercialOfferId: PropTypes.string,
    createSegment: PropTypes.func,
    currentProjectLocation: PropTypes.object,
    drawingType: PropTypes.number,
    editSegmentData: PropTypes.object,
    handleOnClickModule: PropTypes.func,
    handleOnCopy: PropTypes.func,
    handleOnPaste: PropTypes.func,
    isDisabledEdition: PropTypes.bool,
    isDisablingModules: PropTypes.bool,
    isDrawingMode: PropTypes.bool,
    isEditionMode: PropTypes.bool,
    isEnabledSimulation: PropTypes.bool,
    myForwardedRef: PropTypes.object,
    onOpenDrawer: PropTypes.func,
    prepareEditSegment: PropTypes.func,
    resetForm: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    selectSegmentById: PropTypes.func,
    setIsDisablingModules: PropTypes.func,
    setSelectedItems: PropTypes.func,
    sunPosition: PropTypes.object,
    updateAzimuth: PropTypes.func,
    updateFallOrientation: PropTypes.func,
    viewLayersStatus: PropTypes.bool,
};

const ComposedMyComponent = compose(
    connect(mapStateToProps, mapDispatchToProps),
)(MapWrapper);

const MapWrapperForwared = forwardRef((props, ref) => (
    <ComposedMyComponent {...props} myForwardedRef={ref} />
));

MapWrapperForwared.displayName = 'MapWrapperForwared';

export default MapWrapperForwared;
